import { useCallback, useEffect, useState } from 'react';

type GenericFunction = (...args: any[]) => any;

/**
 * Accepts a function `fn` and returns a new function which will
 * call `fn` after the current render cycle has completed.
 *
 * This allows us to ensure that the function's dependencies are updated when it is called.
 * Useful when the function is called immediately after its dependency is updated, e.g. by a redux dispatch
 *
 * Note - if `fn` is called with `null`, it will not be called. You can pass the params as an object to work around this if needed.
 *
 * @example
 * const val = useSelector(selectVal);
 *
 * const fnDeferred = useCallbackDeferred(() => doSomething(val), [val]);
 *
 * useCallback(() => {
 *   dispatch(updateVal('newValue'));
 *   // Even though we just called dispatch, fn will use 'newValue' for val
 *   fnDeferred(val);
 * }, [fnDeferred]);
 */
export const useCallbackDeferred = <T extends GenericFunction>(fn: T, deps: React.DependencyList) => {
  const [callbackParams, setCallbackParams] = useState<null | Parameters<T>>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- can't statically analyze the deps
  const callback = useCallback(fn, deps);

  useEffect(() => {
    if (callbackParams === null) {
      return;
    }

    setCallbackParams(null);
    callback(...callbackParams);
  }, [callback, callbackParams]);

  return useCallback((...args: Parameters<T>) => {
    setCallbackParams(args);
  }, []);
};
